// eslint-disable-next-line import/no-extraneous-dependencies
import { Line } from 'react-chartjs-2';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Paper, Skeleton, Typography } from '@mui/material';

import { chartColors, translateDay } from '~/shared/utils/charts';
import { DashCampaignsModelView } from '~/types';

interface LineChartProps {
  data: DashCampaignsModelView;
  isLoading: boolean;
}

function ChartLegend() {
  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'text.secondary',
          fontSize: '12px',
        }}
      >
        <InfoOutlined sx={{ fontSize: 16 }} />
        <Typography variant="body2">
          Os valores do gráfico representam a média de cada dia da semana nos{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            últimos 90 dias
          </Box>
          .
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: chartColors.line.campaigns.border,
            }}
          />
          <Typography variant="body2">Campanhas ativas</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: chartColors.line.impressions.border,
            }}
          />
          <Typography variant="body2">Impressões</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function LineChart({ data, isLoading }: LineChartProps) {
  if (isLoading) {
    return (
      <Paper sx={{ flex: '1 1 300px' }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            padding: '24px',
            height: '340px',
          }}
        >
          <Skeleton
            variant="text"
            sx={{ fontSize: '1.25rem', mb: 2, width: '200px' }}
          />
          <Skeleton
            variant="rectangular"
            sx={{
              height: '180px',
              borderRadius: '4px',
              mb: 2,
            }}
          />
          <Skeleton variant="text" sx={{ fontSize: '0.875rem', mb: 1 }} />
          <Skeleton
            variant="text"
            sx={{ fontSize: '0.875rem', width: '60%' }}
          />
        </Box>
      </Paper>
    );
  }

  const lineData = {
    labels: (data?.newCampaign?.days ?? []).map(day =>
      translateDay(day?.day ?? '')
    ),
    datasets: [
      {
        label: 'Campanhas ativas',
        data: (data?.newCampaign?.days ?? []).map(
          day => day?.activeCampaigns ?? 0
        ),
        borderColor: chartColors.line.campaigns.border,
        backgroundColor: chartColors.line.campaigns.background,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Impressões',
        data: (data?.newCampaign?.days ?? []).map(day => day?.impressions ?? 0),
        borderColor: chartColors.line.impressions.border,
        backgroundColor: chartColors.line.impressions.background,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  return (
    <Paper sx={{ flex: '1 1 300px' }}>
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          padding: '24px',
          height: '340px',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
          Taxa de novas campanhas
        </Typography>
        <Box
          sx={{
            position: 'relative',
            height: '180px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Line
            data={lineData}
            width={300}
            height={180}
            options={{
              responsive: false,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </Box>
        <ChartLegend />
      </Box>
    </Paper>
  );
}
